import { get } from '@shared/adapters/lodash'
import type { Transaction, TransactionStatus } from '../../transactions/types/transactionTypes'
import type { CryptoTransactionWireDto, CryptoWalletWireDto } from './cryptoWalletContracts'
import type { CryptoWallet, CryptoWalletState } from '../types/cryptoWalletTypes'

const XRPSCAN_ACCOUNT_URL = 'https://xrpscan.com/account/'
const MAX_TOKEN_DECIMALS = 8

const STATUS_MAP: Record<string, TransactionStatus> = {
  completed: 'completed',
  processing: 'processing',
  pending: 'pending',
  failed: 'failed',
}

// Only `active` is a real state for the UI; pending_validation,
// validation_failed and deactivated all mean "address on file, not usable yet",
// which the screens treat identically.
function mapWalletState(status: string, hasAddress: boolean): CryptoWalletState {
  if (!hasAddress) return 'none'
  return status.toLowerCase() === 'active' ? 'active' : 'pending'
}

/** What an investor with no registered XRPL address looks like to the UI. */
export const EMPTY_CRYPTO_WALLET: CryptoWallet = {
  id: '',
  walletAddress: '',
  explorerUrl: '',
  blockchainNetwork: 'XRPL',
  state: 'none',
  statusLabel: '',
  isPrimary: false,
}

export function parseCryptoWallet(resp: unknown): CryptoWallet {
  const raw = (get(resp, 'data') ?? {}) as CryptoWalletWireDto
  const walletAddress = raw.walletAddress ?? ''
  const statusLabel = String(raw.walletStatus ?? '')

  return {
    id: raw.id ?? '',
    walletAddress,
    // The service may omit the explorer link; xrpscan on the r-address is the
    // same destination, so the buttons stay usable either way.
    explorerUrl: raw.addressUrl ?? (walletAddress ? `${XRPSCAN_ACCOUNT_URL}${walletAddress}` : ''),
    blockchainNetwork: raw.blockchainNetwork ?? 'XRPL',
    state: mapWalletState(statusLabel, Boolean(walletAddress)),
    statusLabel,
    isPrimary: Boolean(raw.isPrimary),
  }
}

/** `"150.00000000"` → `"150"`, `"0.50000000"` → `"0.5"`. */
function formatTokenQuantity(quantity: string): string {
  const parsed = Number(quantity)
  if (Number.isNaN(parsed)) return quantity
  return parsed
    .toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: MAX_TOKEN_DECIMALS,
    })
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '')
}

/**
 * Token rows render through the shared activity list, so they are adapted into
 * the same `Transaction` view model the fiat ledger uses. `token_received`
 * drives the diamond badge; the amount carries the token symbol instead of a
 * currency code (e.g. `"+150 BURJVISTA01"`).
 */
export function parseCryptoTransaction(raw: CryptoTransactionWireDto): Transaction {
  const incoming = (raw.transactionType ?? 'credit') === 'credit'
  const createdAt = raw.createdAt ?? ''
  const date = createdAt ? new Date(createdAt) : null
  const quantity = formatTokenQuantity(raw.tokenQuantity ?? '0')
  const symbol = raw.tokenSymbol ?? ''

  return {
    id: raw.id ?? '',
    type: 'token_received',
    title: raw.propertyName || symbol || 'Token transaction',
    // The design writes every token row's leg as "Investment"; a debit is
    // tokens leaving the wallet, which is not an investment.
    category: incoming ? 'Investment' : 'Transfer',
    date: date
      ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '',
    time: date
      ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      : undefined,
    createdAt,
    referenceId: raw.orderReference ?? raw.id ?? null,
    amount: `${incoming ? '+' : '-'}${quantity}${symbol ? ` ${symbol}` : ''}`,
    incoming,
    status: STATUS_MAP[String(raw.status ?? '').toLowerCase()] ?? 'pending',
    propertyName: raw.propertyName ?? null,
  }
}

export interface ParsedCryptoTransactionsPage {
  rows: Transaction[]
  /** True when the service signalled there is nothing after this page. */
  isLastPage: boolean
}

export function parseCryptoTransactions(
  resp: unknown,
  limit: number,
): ParsedCryptoTransactionsPage {
  const data = get(resp, 'data')
  // Bare array today; tolerate a `{ rows }` envelope if one is added later.
  const rows: CryptoTransactionWireDto[] = Array.isArray(data)
    ? (data as CryptoTransactionWireDto[])
    : ((get(data, 'rows') ?? []) as CryptoTransactionWireDto[])

  const totalPages = get(resp, 'pagination.totalPages')
  const page = get(resp, 'pagination.page')

  return {
    rows: rows.map(parseCryptoTransaction),
    // With no pagination envelope, a short page is the only end-of-list signal.
    isLastPage:
      typeof totalPages === 'number' && typeof page === 'number'
        ? page >= totalPages
        : rows.length < limit,
  }
}
